import { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { Category, isCategoryUncategorized } from 'entities/Category';

const compareAlphabetically = (a: string, b: string): number =>
    a.localeCompare(b, undefined, { sensitivity: 'base' });

const compareCategoryIds = (
    aId: string,
    bId: string,
    categories?: Record<string, Category>,
): number => {
    const aTitle = categories?.[aId]?.title || '';
    const bTitle = categories?.[bId]?.title || '';

    const aIsUncategorized = isCategoryUncategorized(aTitle);
    const bIsUncategorized = isCategoryUncategorized(bTitle);

    if (aIsUncategorized !== bIsUncategorized) {
        return aIsUncategorized ? -1 : 1;
    }

    const titleComparison = compareAlphabetically(aTitle, bTitle);
    if (titleComparison !== 0) {
        return titleComparison;
    }

    return compareAlphabetically(aId, bId);
};

const sortOrderItemsByTitle = (items: CatalogItem[]): CatalogItem[] => {
    return [...items].sort((a, b) => {
        const titleComparison = compareAlphabetically(
            a.title || '',
            b.title || '',
        );

        if (titleComparison !== 0) {
            return titleComparison;
        }

        return compareAlphabetically(a.id, b.id);
    });
};

export const sortOrderViewGroupedItems = (
    groupedItems: Record<string, CatalogItem[]>,
    categories?: Record<string, Category>,
): [string, CatalogItem[]][] => {
    const categoriesWithSortedItems = Object.entries(groupedItems).map(
        ([categoryId, items]) =>
            [categoryId, sortOrderItemsByTitle(items)] as [
                string,
                CatalogItem[],
            ],
    );

    return categoriesWithSortedItems.sort(([aId], [bId]) =>
        compareCategoryIds(aId, bId, categories),
    );
};

export const sortOrderViewSummaryEntries = <T>(
    summaryByCategories: Record<string, T>,
    categories?: Record<string, Category>,
): [string, T][] => {
    return Object.entries(summaryByCategories).sort(([aId], [bId]) =>
        compareCategoryIds(aId, bId, categories),
    );
};
